import { useMutation } from "@tanstack/react-query";
import {
  createFileRoute,
  useBlocker,
  useNavigate,
} from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { Editor } from "tldraw";

import { ErrorBanner } from "@/components/Callout";
import { CanvasEditorMachine } from "@/components/canvas-editor/canvas-editor-machine";
import {
  languageAnalysisCategories,
  LanguageAnalysisCategory,
} from "@/components/canvas-editor/shapes/shapes";
import { FeedAvatarInput } from "@/components/FeedAvatarInput";
import { FeedDescriptionInput } from "@/components/FeedDescriptionInput";
import { FeedEditorHandle } from "@/components/FeedEditor";
import { FeedForm } from "@/components/FeedForm";
import { FeedTitleInput } from "@/components/FeedTitleInput";
import { WorkbenchPreviewFeedBase } from "@/components/PreviewFeed";
import {
  contentTypes,
  defaultSimpleFeedVersion,
  SimpleEditorContentType,
  SimpleEditorQuickstart,
} from "@/components/SimpleEditor/index";
import { Button, ButtonLink } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { H2 } from "@/components/ui/typography";
import { createFeed, publishFeed } from "@/lib/api";
import { FeedVersion } from "@/lib/types";

export const Route = createFileRoute("/app/_sidebar-layout/create/simple")({
  component: RouteComponent,
  head: () => ({ meta: [{ title: "Create Simple Feed" }] }),
});

function RouteComponent() {
  return null;
  // Publish the feed

  const [step, setStep] = useState(1);
  const [feedVersion, setFeedVersion] = useState<FeedVersion>(
    defaultSimpleFeedVersion,
  );
  const navigate = useNavigate({ from: "/app/create/simple" });
  const feedEditorRef = useRef<FeedEditorHandle>(null!);
  const editorRef = useRef<Editor>(null!);
  const shouldBlock = useRef(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [title, setTitle] = useState("");
  const onSubmit = useMutation({
    mutationFn: async (data: FormData) => {
      const { algorithm } = await createFeed(data);

      if (isPublishing) {
        await publishFeed(algorithm.id);
      }

      return algorithm;
    },
    onSuccess: async (algorithm) => {
      shouldBlock.current = false;
      await navigate({
        to: "/app/feed/$feedId/view",
        params: { feedId: `${algorithm.id}` },
      });
    },
    onError: (error: AxiosError) => console.error(error),
  });

  useBlocker({
    shouldBlockFn: (event) => {
      if (event.action === "REPLACE") {
        return false;
      }

      if (shouldBlock.current) {
        const shouldNav = confirm(
          "Are you sure you want to leave? This will discard all your changes.",
        );
        return !shouldNav;
      }

      return false;
    },
  });

  const previewRef = useRef<HTMLDivElement>(null);
  const [language, setLanguage] = useState<LanguageAnalysisCategory | "any">(
    "English",
  );
  const [contentType, setContentType] =
    useState<SimpleEditorContentType>("Any");

  useEffect(() => {
    if (step === 3) {
      previewRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [step]);

  return (
    <FeedForm
      isCreate={true}
      onSubmit={onSubmit.mutate}
      feedVersion={feedVersion}
      feedEditorRef={feedEditorRef}
      editorRef={editorRef}
    >
      <ErrorBanner error={onSubmit.error} />
      <div className="flex gap-4 border-b-2 border-slate-7 dark:border-slatedark-7 pb-4 self-stretch">
        <FeedAvatarInput />
        <FeedTitleInput className="h-full" value={title} onChange={setTitle} />
      </div>
      <div className="flex items-center">
        <div className="flex-1">
          <div className="flex flex-col gap-1 max-w-[60ch]">
            <Label htmlFor="public-mode">Language</Label>
            <div className="text-sm text-slate-dim">
              Only show posts in the following languages.
            </div>
          </div>
        </div>
        <SimpleSelect
          aria-label="Language options"
          options={[...languageAnalysisCategories, "any"]}
          value={language}
          onValueChange={setLanguage as (value: string) => void}
        />
      </div>
      <div className="flex items-center">
        <div className="flex-1">
          <div className="flex flex-col gap-1 max-w-[60ch]">
            <Label htmlFor="public-mode">Content Type</Label>
            <div className="text-sm text-slate-dim">
              Only show posts of the following content types.
            </div>
          </div>
        </div>
        <SimpleSelect
          aria-label="Content Type options"
          options={contentTypes}
          value={contentType}
          onValueChange={setContentType as (value: string) => void}
        />
      </div>
      <FeedDescriptionInput />

      {step === 1 && (
        <div className="flex justify-between gap-4">
          <div className="flex-1" />
          <Button onClick={() => setStep(2)} disabled={!title}>
            Next
          </Button>
        </div>
      )}

      {step > 1 && (
        <>
          <div className="flex flex-col gap-4">
            <H2 className="mb-2">What's in your feed?</H2>
            <SimpleEditorQuickstart
              onChange={setFeedVersion}
              language={language}
              contentType={contentType}
              onFinish={(feedVersion) => {
                setFeedVersion(feedVersion);
                setStep(3);
              }}
            />
          </div>
        </>
      )}

      {/* eslint-disable-next-line no-constant-binary-expression */}
      {step > 2 && false && (
        <>
          <div className="flex flex-col gap-4">
            <H2 className="mb-2">Preview</H2> {}
            <div
              ref={previewRef}
              className="border border-slate-7 dark:border-slatedark-7 rounded-lg min-h-[200px] max-h-[80vh] h-screen"
            >
              {/* eslint-disable-next-line @eslint-react/no-context-provider */}
              <CanvasEditorMachine.Provider
                options={{
                  input: { visiblePanel: "feed" },
                }}
              >
                <WorkbenchPreviewFeedBase
                  feedId={undefined}
                  feedVersion={feedVersion}
                />
              </CanvasEditorMachine.Provider>
            </div>
          </div>
        </>
      )}

      {step > 2 && (
        <div className="flex justify-between gap-4">
          <div className="flex-1" />
          <div className="flex gap-2">
            <ButtonLink size="lg" variant="ghost" to="/app">
              Cancel
            </ButtonLink>
            <Button
              size="lg"
              variant="outline"
              isLoading={onSubmit.isPending && !isPublishing}
              type="submit"
            >
              Save Draft
            </Button>
            <Button
              size="lg"
              isLoading={onSubmit.isPending && isPublishing}
              type="submit"
              onClick={() => setIsPublishing(true)}
            >
              Publish
            </Button>
          </div>
        </div>
      )}
    </FeedForm>
  );
}
